import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  Grid,
  Chip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useStore from '../store/useStore';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DOW_RU = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const STATUS_COLOR = {
  present: '#22c55e',
  late: '#f59e0b',
  absent: '#ef4444',
  excused: '#94a3b8',
};

export default function AttendanceCalendar({ attendance = [] }) {
  const { language } = useStore();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth()); // 0-indexed

  // Build a map: 'YYYY-MM-DD' -> { status, note }
  const attMap = {};
  attendance.forEach((a) => {
    const d = new Date(a.date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    attMap[key] = { status: a.status, note: a.note };
  });

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const totalDays = lastDay.getDate();

  // Monday-based grid: getDay() returns 0=Sun,1=Mon,...6=Sat → remap to 0=Mon
  const startDow = (firstDay.getDay() + 6) % 7; // offset of first day
  const cells = []; // null = empty, number = day
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const monthLabel = new Date(year, month, 1).toLocaleString(
    language === 'ru' ? 'ru-RU' : 'en-GB',
    { month: 'long', year: 'numeric' }
  );

  // Stats for this month
  const monthKeys = Array.from({ length: totalDays }, (_, i) => {
    const d = i + 1;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  });
  const monthRecords = monthKeys.map((k) => attMap[k]).filter(Boolean);
  const presentCount = monthRecords.filter(
    (r) => r.status === 'present' || r.status === 'late'
  ).length;
  const absentCount = monthRecords.filter((r) => r.status === 'absent').length;
  const rate =
    monthRecords.length > 0
      ? Math.round((presentCount / monthRecords.length) * 100)
      : null;

  const headers = language === 'ru' ? DOW_RU : DOW;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <IconButton size="small" onClick={prevMonth}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, textTransform: 'capitalize' }}
          >
            {monthLabel}
          </Typography>
          {rate !== null && (
            <Typography
              variant="caption"
              color={rate >= 70 ? 'success.main' : 'error.main'}
              sx={{ fontWeight: 600 }}
            >
              {rate}% {language === 'ru' ? 'посещаемость' : 'attendance'} ·{' '}
              {presentCount}/{monthRecords.length}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={nextMonth}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Day-of-week headers */}
      <Grid container columns={7} sx={{ mb: 0.5 }}>
        {headers.map((h) => (
          <Grid size={{ xs: 1 }} key={h}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                fontWeight: 700,
                color: 'text.secondary',
                fontSize: '0.65rem',
              }}
            >
              {h}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Day cells */}
      <Grid container columns={7}>
        {cells.map((day, idx) => {
          if (day === null) return <Grid size={{ xs: 1 }} key={`e-${idx}`} />;
          const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const record = attMap[key];
          const isToday =
            day === now.getDate() &&
            month === now.getMonth() &&
            year === now.getFullYear();

          return (
            <Grid size={{ xs: 1 }} key={key}>
              <Tooltip
                title={
                  record
                    ? `${record.status}${record.note ? ` — ${record.note}` : ''}`
                    : ''
                }
                disableHoverListener={!record}
              >
                <Box
                  sx={{
                    m: 0.3,
                    aspectRatio: '1',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: record
                      ? STATUS_COLOR[record.status]
                      : 'transparent',
                    border: isToday ? '2px solid' : '1px solid',
                    borderColor: record
                      ? STATUS_COLOR[record.status]
                      : isToday
                        ? 'primary.main'
                        : 'divider',
                    cursor: record ? 'pointer' : 'default',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: isToday ? 800 : 500,
                      color: record
                        ? '#fff'
                        : isToday
                          ? 'primary.main'
                          : 'text.secondary',
                      lineHeight: 1,
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
        {[
          {
            status: 'present',
            label: language === 'ru' ? 'Присут.' : 'Present',
          },
          {
            status: 'absent',
            label: language === 'ru' ? 'Отсутств.' : 'Absent',
          },
          { status: 'late', label: language === 'ru' ? 'Опоздал' : 'Late' },
          {
            status: 'excused',
            label: language === 'ru' ? 'Уваж. прич.' : 'Excused',
          },
        ].map(({ status, label }) => (
          <Box
            key={status}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: STATUS_COLOR[status],
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem' }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
